import type { VehicleTypeRow, CompatibilityRow } from "@/lib/vehicleTypes";
import { getCompatibleVehicleTypes } from "@/lib/vehicleTypes";
import type { FleetJobRow, FleetRosterShiftRow, SlotBlock, SlotSheetResult } from "./types";
import { computeJobTimes, timeToHours, trimToHHMM, nzDateStrFromIso, nzHourFromIso, vehicleTypeForRequestedLabel, roundUpToStep } from "./utils";
import type { ConfirmedMultiLegGroup } from "@/lib/multiLegGroups";

// Once a vehicle clears a job (plus its load/unload/spacing buffer), the
// next open slot is rounded up to this mark -- a vehicle isn't really
// getting a fresh booking at an odd time like 11:23, and clean marks read
// far better on the sheet than a different offset for every vehicle.
const NEXT_SLOT_ROUND_H = 10 / 60;

// Used by prepareLaneForDisplay's nominal anchoring when a job has no
// estimated_duration_seconds at all -- mirrors computeJobTimes' own fallback.
const DEFAULT_JOB_DURATION_H = 1 / 3;

/**
 * An ASAP call has no deliberately-chosen minute to respect the way a
 * scheduled booking's requested_pickup_at does -- it's just whenever the
 * phone happened to ring, so anchoring to it as-is would show the same kind
 * of arbitrary, off-grid slot the nominal-anchoring fix above was meant to
 * get away from (e.g. a 7:25 call sitting at its own odd minute instead of
 * a clean slot mark). Rounds to whichever slot boundary on this vehicle's
 * own grid (anchored at its shift start) the raw time is closer to -- at or
 * past the midpoint of a slot rounds forward into the next one, short of
 * it rounds back to the slot's own start.
 */
function roundToNearestSlot(rawH: number, shiftStartH: number, slotWidthH: number): number {
  const slotsFromStart = Math.max(0, (rawH - shiftStartH) / slotWidthH);
  const flooredSlots = Math.floor(slotsFromStart + 1e-9);
  const fraction = slotsFromStart - flooredSlots;
  const roundedSlots = fraction >= 0.5 - 1e-9 ? flooredSlots + 1 : flooredSlots;
  return shiftStartH + roundedSlots * slotWidthH;
}

/**
 * Where a job's slot SHOULD sit for display, ignoring real pu_time/do_time
 * entirely -- the originally requested (or, failing that, assigned) time and
 * its estimated duration, same fields a not-yet-picked-up job already uses.
 * Exists because computeJobTimes (the source of a block's real startH/endH)
 * deliberately switches to real pu_time/do_time once a job reaches that
 * stage, which is correct for placement (the actual buffer math needs the
 * real clock) but wrong for display: a job picked up a few minutes early or
 * late would otherwise visibly creep away from the slot it was booked into,
 * every time, for every job, all day. An ASAP job's nominal start is further
 * rounded to the nearest slot boundary (see roundToNearestSlot) since it has
 * no deliberately-chosen minute to respect in the first place.
 */
function nominalJobSpan(job: FleetJobRow, slotWidthH: number, shiftStartH: number): { startH: number; endH: number } {
  let startH = nzHourFromIso(job.requested_pickup_at) ?? nzHourFromIso(job.assigned_at) ?? (job.pu_time ? timeToHours(trimToHHMM(job.pu_time)) : 0);
  if (job.is_asap) startH = roundToNearestSlot(startH, shiftStartH, slotWidthH);
  const durationH = job.estimated_duration_seconds && job.estimated_duration_seconds > 0 ? job.estimated_duration_seconds / 3600 : DEFAULT_JOB_DURATION_H;
  return { startH, endH: startH + durationH };
}

interface ShiftWithTimes {
  shift: FleetRosterShiftRow;
  startH: number;
  endH: number;
}

interface JobWithTimes {
  job: FleetJobRow;
  startH: number;
  endH: number;
}

function shiftsForDate(shifts: FleetRosterShiftRow[], dateStr: string): ShiftWithTimes[] {
  return shifts
    .filter((s) => s.shift_date === dateStr)
    .map((s) => {
      const startH = timeToHours(trimToHHMM(s.start_hhmm));
      let endH = timeToHours(trimToHHMM(s.end_hhmm));
      if (endH <= startH) endH += 24; // overnight shift
      return { shift: s, startH, endH };
    })
    .sort((a, b) => a.startH - b.startH);
}

/**
 * Greedy interval-graph colouring, same principle as job-lane packing: each
 * shift goes into the first lane whose previous shift has already ended.
 * Lanes come out as chronologically ordered, non-overlapping sequences of
 * real shifts -- which is what lets each slot show a real vehicle number and
 * real shift-start/shift-end markers, not just an abstract time window.
 */
function packShiftsIntoLanes(shiftsWithTimes: ShiftWithTimes[]): ShiftWithTimes[][] {
  const laneEndTimes: number[] = [];
  const lanes: ShiftWithTimes[][] = [];
  for (const sw of shiftsWithTimes) {
    let lane = laneEndTimes.findIndex((end) => end <= sw.startH);
    if (lane === -1) {
      lane = laneEndTimes.length;
      lanes.push([]);
      laneEndTimes.push(sw.endH);
    } else {
      laneEndTimes[lane] = sw.endH;
    }
    lanes[lane].push(sw);
  }
  return lanes;
}

/**
 * `assigned_at` is a UTC instant with no inherent notion of "which side of
 * midnight" relative to the job's own decimal-hour `jobStartH` (which may
 * already be shifted +24 for an overnight shift) -- picks whichever of
 * {as-is, +24, -24} lands closest to jobStartH, since a real assignment is
 * always shortly before its own pickup, never a full day off.
 */
function assignedAtStartH(job: FleetJobRow, jobStartH: number): number | null {
  const h = nzHourFromIso(job.assigned_at);
  if (h === null) return null;
  const candidates = [h, h + 24, h - 24];
  return candidates.reduce((best, c) => (Math.abs(c - jobStartH) < Math.abs(best - jobStartH) ? c : best));
}

/**
 * Fixed-interval slots across one shift's span -- one every `tickH` from
 * when the vehicle comes on to when it finishes, e.g. a 7:30 start with a
 * 15-minute slot length gets slots at 7:30, 7:45, 8:00... A job occupies a
 * single slot at its real time; ticking resumes after the job ends plus the
 * vehicle type's buffer (that buffer already covers travel/turnaround), then
 * rounds up to the next 10-minute mark so the next open slot reads clean
 * (e.g. clear at 11:23 -> next slot shown as 11:30) instead of a different
 * odd offset per vehicle. First slot of the shift is flagged as the start,
 * last as the end.
 *
 * A job assigned meaningfully ahead of its own pickup time (e.g. a
 * long-distance job dispatched an hour early so the driver has travel time)
 * gets a "committed" block for that lead window instead of open gap ticks --
 * the vehicle isn't really free there, even though no pickup is happening
 * yet. That window is clamped to never start before the cursor already is,
 * so a job assigned WHILE a prior job is still running (a normal dispatch
 * pattern, not a conflict) never overlaps that prior job -- the vehicle just
 * rolls straight from one into the other with no gap shown in between.
 */
function ticksForShift(
  sw: ShiftWithTimes,
  jobsForShift: JobWithTimes[],
  lane: number,
  tickH: number,
  requiredGapH: number
): SlotBlock[] {
  const blocks: SlotBlock[] = [];
  const vehicleNumber = sw.shift.vehicle_number;
  let cursor = sw.startH;

  for (const j of [...jobsForShift].sort((a, b) => a.startH - b.startH)) {
    // Only a job that HASN'T been picked up yet (and didn't end as a
    // no-show) has a "committed, heading there" window worth showing --
    // once pu_time is real, `startH` is the actual historical pickup, and a
    // driver is always assigned before they genuinely pick someone up, so
    // this comparison would otherwise flag almost every completed job in
    // the day, not just genuinely upcoming ones.
    const isHistorical = !!j.job.pu_time || j.job.Stage === "No show";
    const assignedH = isHistorical ? null : assignedAtStartH(j.job, j.startH);
    const committedFromH = assignedH !== null && assignedH < j.startH - 1e-9 ? Math.max(assignedH, cursor) : null;
    const openUntilH = Math.min(committedFromH ?? j.startH, j.startH);

    while (cursor < openUntilH - 1e-9) {
      const tickEnd = Math.min(cursor + tickH, openUntilH);
      blocks.push({ kind: "gap", lane, startH: cursor, endH: tickEnd, vehicleNumber, isShiftStart: false, isShiftEnd: false, shiftStartH: sw.startH });
      cursor = tickEnd;
    }

    if (committedFromH !== null && committedFromH < j.startH - 1e-9) {
      blocks.push({ kind: "committed", job: j.job, lane, startH: committedFromH, endH: j.startH, vehicleNumber, isShiftStart: false, isShiftEnd: false, shiftStartH: sw.startH });
      cursor = j.startH;
    }

    blocks.push({
      kind: "job",
      job: j.job,
      lane,
      startH: j.startH,
      endH: j.endH,
      vehicleNumber,
      isShiftStart: false,
      isShiftEnd: false,
      shiftStartH: sw.startH,
    });
    // The buffer between a job ending and the next tick's rounded-up start
    // is real time too -- represented as its own (short) gap rather than
    // skipped, so the lane's coverage never has a hole. "now" tracking (see
    // FleetSlotSheet) depends on every moment being covered by some block;
    // without this, the highlight and Jump to Now silently do nothing
    // whenever the current time falls in that buffer window.
    const bufferStart = Math.max(cursor, j.endH);
    const bufferEnd = roundUpToStep(Math.max(cursor, j.endH + requiredGapH), NEXT_SLOT_ROUND_H);
    if (bufferEnd > bufferStart + 1e-9) {
      blocks.push({ kind: "gap", lane, startH: bufferStart, endH: bufferEnd, vehicleNumber, isShiftStart: false, isShiftEnd: false, shiftStartH: sw.startH });
    }
    cursor = bufferEnd;
  }

  while (cursor < sw.endH - 1e-9) {
    const tickEnd = Math.min(cursor + tickH, sw.endH);
    blocks.push({ kind: "gap", lane, startH: cursor, endH: tickEnd, vehicleNumber, isShiftStart: false, isShiftEnd: false, shiftStartH: sw.startH });
    cursor = tickEnd;
  }

  if (blocks.length > 0) {
    blocks[0] = { ...blocks[0], isShiftStart: true };
    blocks[blocks.length - 1] = { ...blocks[blocks.length - 1], isShiftEnd: true };
  }
  return blocks;
}

/**
 * Builds a roster-driven slot sheet for one vehicle type/day. Slots are
 * fixed-interval ticks across each real shift's span (see ticksForShift),
 * packed into lanes by real shift (see packShiftsIntoLanes) so each slot can
 * carry a real vehicle number and shift start/end markers. Jobs are matched
 * to their shift by vehicle number + time; a job whose vehicle wasn't
 * rostered at that time (a data mismatch) is flagged into an overflow lane
 * rather than dropped.
 */
export function buildSlotSheet(
  shifts: FleetRosterShiftRow[],
  jobs: FleetJobRow[],
  dateStr: string,
  vehicleType: VehicleTypeRow
): SlotSheetResult {
  const dayShifts = shiftsForDate(shifts, dateStr);
  // Cancelled bookings never happened -- the vehicle was never actually
  // held up by them, so they shouldn't block a slot as if they were real
  // work. Excluded here rather than filtered out of the query so every
  // other screen reading taxicaller_data (payments, driver history) still
  // sees the cancellation.
  const dayJobs: JobWithTimes[] = jobs
    .filter((j) => j.pu_date === dateStr && j.Stage !== "Cancelled")
    .map((job) => ({ job, ...computeJobTimes(job) }));

  if (dayShifts.length === 0) {
    // No rostered shift of this type today doesn't mean no work -- e.g. an
    // STS booking with no STS_* shift formally rostered for it yet. Surface
    // whatever jobs exist as a single overflow row instead of hiding real
    // bookings just because there's no lane to place them in.
    if (dayJobs.length === 0) {
      return { laneCount: 0, lanes: [], hasOverflow: false };
    }
    const overflowJobs = [...dayJobs].sort((a, b) => a.startH - b.startH);
    return {
      laneCount: 1,
      lanes: [
        overflowJobs.map((dj) => ({
          kind: "job" as const,
          job: dj.job,
          lane: 0,
          startH: dj.startH,
          endH: dj.endH,
          vehicleNumber: dj.job.vehicle_number,
          isShiftStart: false,
          isShiftEnd: false,
          shiftStartH: dj.startH,
        })),
      ],
      hasOverflow: true,
    };
  }

  const laneShifts = packShiftsIntoLanes(dayShifts);
  const maxSlots = laneShifts.length;

  const tickH = Math.max(vehicleType.slot_length_minutes || 15, 1) / 60;
  const requiredGapH =
    Math.max(
      (vehicleType.load_buffer_minutes || 0) + (vehicleType.unload_buffer_minutes || 0),
      vehicleType.min_spacing_minutes || 0
    ) / 60;

  const matchedJobIds = new Set<string>();
  const jobsForShift = (sw: ShiftWithTimes): JobWithTimes[] => {
    if (!sw.shift.vehicle_number) return [];
    const matches = dayJobs.filter(
      (dj) => dj.job.vehicle_number === sw.shift.vehicle_number && dj.startH >= sw.startH && dj.startH <= sw.endH
    );
    matches.forEach((m) => matchedJobIds.add(m.job.id));
    return matches;
  };

  const lanes: SlotBlock[][] = laneShifts.map((shiftsInLane, lane) =>
    shiftsInLane.flatMap((sw) => ticksForShift(sw, jobsForShift(sw), lane, tickH, requiredGapH))
  );

  const overflowJobs = dayJobs.filter((dj) => !matchedJobIds.has(dj.job.id)).sort((a, b) => a.startH - b.startH);
  const hasOverflow = overflowJobs.length > 0;
  if (hasOverflow) {
    lanes.push(
      overflowJobs.map((dj) => ({
        kind: "job" as const,
        job: dj.job,
        lane: maxSlots,
        startH: dj.startH,
        endH: dj.endH,
        vehicleNumber: dj.job.vehicle_number,
        isShiftStart: false,
        isShiftEnd: false,
        // A real (already-dispatched) booking mismatched to no rostered shift --
        // not placed via the gap search below, so no meaningful shift start to record.
        shiftStartH: dj.startH,
      }))
    );
  }

  return { laneCount: lanes.length, lanes, hasOverflow };
}

export interface TypedSlotSheet {
  type: VehicleTypeRow;
  sheet: SlotSheetResult;
}

export interface ScheduledBreakForPlacement {
  vehicleNumber: string;
  driverName: string;
  startH: number;
  endH: number;
  taken: boolean;
}

/**
 * Overlays published scheduled breaks onto whichever gap slot(s) they cover,
 * splitting/trimming the surrounding gap the same way placeUnassignedJobs
 * does for a booking -- except a break already knows its own vehicle, so
 * there's no type-compatibility fallback search, just a direct match on
 * vehicle number within that vehicle's own lane.
 */
export function placeScheduledBreaks(
  perTypeSheets: TypedSlotSheet[],
  breaks: ScheduledBreakForPlacement[]
): TypedSlotSheet[] {
  const working = perTypeSheets.map(({ type, sheet }) => ({
    type,
    lanes: sheet.lanes.map((lane) => [...lane]),
    hasOverflow: sheet.hasOverflow,
  }));

  for (const brk of breaks) {
    outer: for (const w of working) {
      for (const lane of w.lanes) {
        const blockIdx = lane.findIndex(
          (b) => b.kind === "gap" && b.vehicleNumber === brk.vehicleNumber && b.startH <= brk.startH + 1e-9 && b.endH > brk.startH + 1e-9
        );
        if (blockIdx === -1) continue;

        const gap = lane[blockIdx];
        const before: SlotBlock[] = gap.startH < brk.startH - 1e-9 ? [{ ...gap, endH: brk.startH, isShiftEnd: false }] : [];
        // The tail of the gap tick the break was placed into, if the break
        // is shorter than that tick -- kept as its own gap instead of being
        // silently dropped, same reasoning as placeUnassignedJobs: the
        // lane's time coverage must stay gapless for "now" tracking to work.
        const after: SlotBlock[] = gap.endH > brk.endH + 1e-9 ? [{ ...gap, startH: brk.endH, isShiftStart: false }] : [];
        const breakBlock: SlotBlock = {
          kind: "break",
          driverName: brk.driverName,
          taken: brk.taken,
          lane: gap.lane,
          startH: brk.startH,
          endH: brk.endH,
          vehicleNumber: gap.vehicleNumber,
          isShiftStart: false,
          isShiftEnd: false,
          shiftStartH: gap.shiftStartH,
        };
        const replacement = [...before, breakBlock, ...after];
        lane.splice(blockIdx, 1, ...replacement);

        const i = blockIdx + replacement.length;
        while (i < lane.length && lane[i].kind === "gap" && lane[i].startH < brk.endH - 1e-9) {
          if (lane[i].endH <= brk.endH + 1e-9) {
            lane.splice(i, 1);
          } else {
            lane[i] = { ...lane[i], startH: brk.endH };
            break;
          }
        }
        break outer;
      }
    }
  }

  return working.map((w) => ({ type: w.type, sheet: { laneCount: w.lanes.length, lanes: w.lanes, hasOverflow: w.hasOverflow } }));
}

/**
 * Display-only re-grouping of a lane's blocks, applied at render time (see
 * FleetSlotSheet.tsx / DriverFleetSlotsPanel.tsx) -- never part of the
 * placement pipeline above, and never fed back into it. A dispatcher can
 * manually place a real, known booking (e.g. a second job) into a tight real
 * gap right after another job -- that's exactly what placeUnassignedJobs
 * above already does correctly. This function just changes how that gets
 * DRAWN: each job/break/committed block is padded out to a whole number of
 * the vehicle type's slot-widths (measured from its own start), so a short
 * job doesn't read as leaving a sliver of "available" time that isn't
 * genuinely offerable to a new, unknown booking. If another real block
 * follows before the padding would end, the padding simply stops there (no
 * gap row invented in between) and that next block renders at its own real
 * time, right after -- so two tight back-to-back real bookings still both
 * show, just with nothing awkward drawn between them. A block whose own
 * span is longer than one slot-width is split into that many numbered
 * sub-rows (1/3, 2/3, 3/3...) instead of one oversized row. Job blocks are
 * also re-anchored to their nominal (booked) time/duration rather than their
 * real one first (see nominalJobSpan) -- so a job picked up/dropped off a
 * few minutes early or late still shows in its originally-booked slot at its
 * originally-expected width, not a slightly different one every time.
 */
export function prepareLaneForDisplay(inputLane: SlotBlock[], slotLengthMinutes: number): SlotBlock[] {
  const slotWidthH = Math.max(slotLengthMinutes || 15, 1) / 60;
  // Local working copy -- never mutate the caller's array/blocks, since it's
  // the same lane array the placement pipeline's memoized result owns.
  const lane = inputLane.slice();

  // Pass 1: re-anchor every job block to its nominal (booked) time instead
  // of its real one, so a job that's been picked up/dropped off a few
  // minutes early or late doesn't visibly drift off its originally-booked
  // slot (see nominalJobSpan). Clamped to never start before whatever real,
  // fixed commitment (another job, a break, an early-assignment lead
  // window) already precedes it in this lane -- a job that ran meaningfully
  // LATE in reality can't nominally retreat into time that's already
  // accounted for; that rare case just falls back to showing at the
  // earliest time actually available. Gap blocks are deliberately NOT used
  // as a floor here: a plain gap tick's real boundary always happens to end
  // exactly at whatever REAL job followed it, so treating that as a floor
  // would silently re-import the real time we're trying to get away from.
  let prevEndH = -Infinity;
  for (let idx = 0; idx < lane.length; idx++) {
    const b = lane[idx];
    if (b.kind === "gap") continue;
    if (b.kind !== "job") {
      prevEndH = Math.max(prevEndH, b.endH);
      continue;
    }
    if (b.legIndex != null) {
      // One leg of a confirmed multi-leg group -- startH/endH were already
      // correctly sequenced (including the inter-leg buffer) at placement
      // time in placeUnassignedJobs. Re-deriving from nominalJobSpan would
      // collapse every leg back onto the same instant, since a multi-leg
      // booking's legs commonly all share one requested_pickup_at. Still
      // floor-clamp to prevEndH so a fixed, earlier commitment already in
      // this lane can't be silently overlapped.
      const startH = Math.max(b.startH, prevEndH);
      const endH = Math.max(b.endH, startH + 1e-6);
      lane[idx] = { ...b, startH, endH };
      prevEndH = endH;
      continue;
    }
    const nominal = nominalJobSpan(b.job, slotWidthH, b.shiftStartH);
    const startH = Math.max(nominal.startH, prevEndH);
    const endH = Math.max(nominal.endH, startH + 1e-6);
    lane[idx] = { ...b, startH, endH };
    prevEndH = endH;
  }

  // Pass 2: rebuild gap coverage to exactly fill whatever's left between the
  // now nominal-anchored occupied blocks. Pass 1 only moves job blocks, so a
  // real gap tick's original boundary can be left stale on either side (too
  // early if the job moved later, too late -- even overlapping -- if it
  // moved earlier); every run of consecutive gap blocks is collapsed into a
  // single gap spanning precisely [previous block's end, next block's
  // start), dropped entirely if that span is no longer positive.
  const reconciled: SlotBlock[] = [];
  let idx2 = 0;
  while (idx2 < lane.length) {
    const b = lane[idx2];
    if (b.kind !== "gap") {
      reconciled.push(b);
      idx2++;
      continue;
    }
    let j2 = idx2;
    while (j2 < lane.length && lane[j2].kind === "gap") j2++;
    const prevBlock = reconciled[reconciled.length - 1];
    const nextBlock = j2 < lane.length ? lane[j2] : null;
    const fromH = prevBlock ? prevBlock.endH : b.startH;
    const toH = nextBlock ? nextBlock.startH : lane[j2 - 1].endH;
    if (toH > fromH + 1e-9) {
      reconciled.push({ ...b, startH: fromH, endH: toH, isShiftStart: lane[idx2].isShiftStart, isShiftEnd: lane[j2 - 1].isShiftEnd });
    }
    idx2 = j2;
  }

  const out: SlotBlock[] = [];
  let i = 0;
  while (i < reconciled.length) {
    const block = reconciled[i];
    if (block.kind === "gap") {
      // Split into slot-width ticks too, not left as one big collapsed row
      // -- a dispatcher scanning for "do I have a slot at 6:30?" needs an
      // actual row at 6:30, not a single multi-hour block they'd have to do
      // arithmetic on. Pass 2 already gave this gap its correct, fully
      // reconciled [startH, endH) span, so there's nothing else to look
      // ahead for here (unlike occupied blocks below, which pad into
      // whatever real gap follows).
      const slotTotal = Math.max(1, Math.ceil((block.endH - block.startH) / slotWidthH - 1e-9));
      let cursor = block.startH;
      for (let idx = 1; idx <= slotTotal; idx++) {
        const segEnd = idx < slotTotal ? cursor + slotWidthH : block.endH;
        out.push({
          ...block,
          startH: cursor,
          endH: segEnd,
          slotIndex: idx,
          slotTotal,
          isShiftStart: idx === 1 ? block.isShiftStart : false,
          isShiftEnd: idx === slotTotal ? block.isShiftEnd : false,
        });
        cursor = segEnd;
      }
      i++;
      continue;
    }

    const slotTotal = Math.max(1, Math.ceil((block.endH - block.startH) / slotWidthH - 1e-9));
    const paddedEndH = block.startH + slotTotal * slotWidthH;

    // The next real (non-gap) block in this lane, if any -- blocks never
    // overlap, so its start is always >= this block's own real end.
    let j = i + 1;
    while (j < reconciled.length && reconciled[j].kind === "gap") j++;
    const nextOccupiedStartH = j < reconciled.length ? reconciled[j].startH : Infinity;
    const displayEndH = Math.min(paddedEndH, nextOccupiedStartH);

    // Absorb (drop or trim) whatever gap blocks fall within [block.endH, displayEndH) --
    // fully dropped if entirely covered, trimmed to start at displayEndH if only partly.
    let k = i + 1;
    while (k < j && reconciled[k].endH <= displayEndH + 1e-9) k++;
    if (k < j && reconciled[k].startH < displayEndH - 1e-9) {
      reconciled[k] = { ...reconciled[k], startH: displayEndH };
    } else {
      k = Math.min(k, j);
    }
    // If displayEndH reached all the way to the next occupied block, that
    // block's gap tail (if any partial one remained) has nothing left to
    // trim -- continue straight from j.
    const resumeAt = displayEndH >= nextOccupiedStartH - 1e-9 ? j : k;

    let cursor = block.startH;
    for (let idx = 1; idx <= slotTotal; idx++) {
      const segEnd = idx < slotTotal ? cursor + slotWidthH : displayEndH;
      out.push({
        ...block,
        startH: cursor,
        endH: segEnd,
        slotIndex: idx,
        slotTotal,
        isShiftStart: idx === 1 ? block.isShiftStart : false,
        isShiftEnd: idx === slotTotal ? block.isShiftEnd : false,
      } as SlotBlock);
      cursor = segEnd;
    }
    i = resumeAt;
  }
  return out;
}

/**
 * Not-yet-assigned prebookings (vehicle_number is null -- TaxiCaller hasn't
 * dispatched a vehicle to them yet) aren't visible to buildSlotSheet at all,
 * since it only ever matches jobs to a shift by vehicle_number. This is a
 * separate pass, run once across every type's already-built sheet, that
 * tries to seat each one into a genuinely open gap slot -- its own vehicle
 * type first, then whatever other types are configured in Settings ->
 * Vehicle Types -> Compatibility as able to cover that job type (e.g. an
 * SUV-class booking can be seated in a Sedan's open slot if that's
 * configured) -- before ever falling back to that type's overflow lane.
 * Earliest-requested job is seated first (simple first-come-first-served).
 */
// A driver needs a few minutes after clocking on before they can realistically
// be dispatched to a pickup -- logging into the app, doing a start-of-shift
// check, receiving and acknowledging the job. A vehicle whose shift starts at
// the same moment as (or within this window of) a pickup isn't a real
// candidate for it, even though the raw shift/tick bounds nominally overlap.
const DRIVER_LOGON_LEAD_H = 15 / 60;

export function placeUnassignedJobs(
  perTypeSheets: TypedSlotSheet[],
  unassignedJobs: FleetJobRow[],
  compatMatrix: CompatibilityRow[],
  dateStr: string,
  confirmedMultiLegGroups: ConfirmedMultiLegGroup[] = [],
  multiLegBufferMinutes = 5
): TypedSlotSheet[] {
  const working = perTypeSheets.map(({ type, sheet }) => ({
    type,
    lanes: sheet.lanes.map((lane) => [...lane]),
    hasOverflow: sheet.hasOverflow,
    // Round-robins which lane gets first refusal within this type, so
    // same-time unassigned bookings spread across available vehicles
    // instead of always stacking onto whichever lane comes first.
    nextLaneIdx: 0,
    // Same load/unload + spacing buffer ticksForShift applies after a real
    // scheduled job -- confirmed missing here entirely, which is why two
    // still-unassigned bookings could get suggested back-to-back on the
    // same vehicle with zero realistic turnaround time between them.
    requiredGapH:
      Math.max(
        (type.load_buffer_minutes || 0) + (type.unload_buffer_minutes || 0),
        type.min_spacing_minutes || 0
      ) / 60,
    // Per-lane buffer windows already claimed by an earlier-placed job in
    // this same pass -- still rendered as an ordinary gap block (so "now"
    // tracking has no coverage hole), but excluded from the search below so
    // a later unassigned job can't get seated into another one's turnaround
    // time just because it's technically still a "gap" block.
    reservedByLane: new Map<number, { startH: number; endH: number }[]>(),
  }));
  const byTypeName = new Map(working.map((w) => [w.type.vehicle_type, w]));

  const dayJobs = unassignedJobs.filter((j) => nzDateStrFromIso(j.requested_pickup_at) === dateStr && j.Stage !== "Cancelled");

  // Confirmed multi-leg groups (see multi_leg_groups / src/lib/multiLegGroups.ts)
  // collapse into ONE combined entry here for GAP-SEARCHING purposes -- start
  // at the earliest leg's start, occupying the sum of every leg's own
  // duration plus an inter-leg buffer, so the seating loop below reserves
  // one vehicle's worth of gap instead of one per leg. `legTimes` carries
  // each leg's own start/end through so the vehicle can still be shown busy
  // at each leg's own slot (see buildOccupyingBlocks below), not just one
  // combined row.
  const groupByJobId = new Map<string, ConfirmedMultiLegGroup>();
  confirmedMultiLegGroups.forEach((g) => g.job_ids.forEach((jid) => groupByJobId.set(jid, g)));
  const consumedGroupIds = new Set<string>();

  const sortedJobs: {
    job: FleetJobRow;
    startH: number;
    endH: number;
    legTimes?: { job: FleetJobRow; startH: number; endH: number }[];
    legBufferMinutes?: number;
    // The real vehicle_number already assigned to one or more of this
    // group's legs, if any (see ConfirmedMultiLegGroup) -- when set, the
    // seating loop below attaches the remaining legs to that SAME vehicle
    // instead of searching for a fresh one.
    assignedVehicleNumber?: string | null;
  }[] = [];
  for (const job of dayJobs) {
    const group = job.Job_ID ? groupByJobId.get(job.Job_ID) : undefined;
    if (!group) {
      sortedJobs.push({ job, ...computeJobTimes(job) });
      continue;
    }
    if (consumedGroupIds.has(group.id)) continue; // already emitted this group's combined entry

    const legs = dayJobs
      .filter((j) => j.Job_ID && group.job_ids.includes(j.Job_ID))
      .map((j) => ({ job: j, ...computeJobTimes(j) }))
      .sort((a, b) => a.startH - b.startH);

    if (legs.length < 2) {
      // Only one of this group's legs is actually in today's unassigned
      // set (the rest may already be assigned or closed) -- nothing to combine.
      sortedJobs.push({ job, ...computeJobTimes(job) });
      continue;
    }

    consumedGroupIds.add(group.id);
    const first = legs[0];
    const totalDurationH = legs.reduce((sum, l) => sum + (l.endH - l.startH), 0);
    const perLegBufferMinutes = group.buffer_minutes ?? multiLegBufferMinutes;
    const bufferH = (perLegBufferMinutes * Math.max(0, legs.length - 1)) / 60;
    sortedJobs.push({
      job: first.job,
      startH: first.startH,
      endH: first.startH + totalDurationH + bufferH,
      legTimes: legs,
      legBufferMinutes: perLegBufferMinutes,
      assignedVehicleNumber: group.assigned_vehicle_number,
    });
  }
  sortedJobs.sort((a, b) => a.startH - b.startH);

  // Builds the block(s) that actually occupy the vehicle for one sortedJobs
  // entry -- a single block for an ordinary job, or one block per leg
  // (chained back-to-back with the group's inter-leg buffer) for a confirmed
  // multi-leg group, so the sheet shows the vehicle busy across every real
  // stop instead of one combined row that leaves later slots looking free.
  const buildOccupyingBlocks = (
    dj: (typeof sortedJobs)[number],
    laneIdx: number,
    vehicleNumber: string | null,
    shiftStartH: number,
    leadingIsShiftStart: boolean,
    trailingIsShiftEnd: boolean
  ): SlotBlock[] => {
    if (!dj.legTimes || dj.legTimes.length < 2) {
      return [
        {
          kind: "job",
          job: dj.job,
          lane: laneIdx,
          startH: dj.startH,
          endH: dj.endH,
          vehicleNumber,
          isShiftStart: leadingIsShiftStart,
          isShiftEnd: trailingIsShiftEnd,
          shiftStartH,
        },
      ];
    }
    const bufferH = (dj.legBufferMinutes ?? multiLegBufferMinutes) / 60;
    let cursor = dj.startH;
    const legTimes = dj.legTimes;
    return legTimes.map((lt, idx) => {
      const legDurH = lt.endH - lt.startH;
      const blockStart = cursor;
      const blockEnd = blockStart + legDurH;
      cursor = blockEnd + bufferH;
      return {
        kind: "job",
        job: lt.job,
        lane: laneIdx,
        startH: blockStart,
        endH: blockEnd,
        vehicleNumber,
        isShiftStart: idx === 0 && leadingIsShiftStart,
        isShiftEnd: idx === legTimes.length - 1 && trailingIsShiftEnd,
        shiftStartH,
        legIndex: idx + 1,
        legTotal: legTimes.length,
      } as SlotBlock;
    });
  };

  // Tries to seat `dj` into a specific (type, lane) -- the shared gap-search
  // + splice logic used by both the normal round-robin search below and the
  // assigned-vehicle-first search for a partially-dispatched multi-leg group.
  const trySeatInLane = (w: (typeof working)[number], laneIdx: number, dj: (typeof sortedJobs)[number]): boolean => {
    const lane = w.lanes[laneIdx];
    // Tie-break exact boundary times (e.g. a booking at exactly :00) toward
    // the gap that STARTS at that hour, not the one ending there -- testing
    // both sides against the same shifted point (rather than widening the
    // interval on both ends) means a real tick boundary can never satisfy
    // both the earlier and later gap. Keep in lockstep with
    // calculate-capacity-slot/index.ts. Also requires the vehicle to have
    // been on shift at least the driver log-on lead time before this
    // pickup -- a shift that only just started can't realistically take it.
    const reserved = w.reservedByLane.get(laneIdx) || [];
    const blockIdx = lane.findIndex(
      (b) =>
        b.kind === "gap" &&
        b.startH <= dj.startH + 1e-9 &&
        b.endH > dj.startH + 1e-9 &&
        dj.startH - b.shiftStartH >= DRIVER_LOGON_LEAD_H - 1e-9 &&
        !reserved.some((r) => dj.startH < r.endH - 1e-9 && dj.endH > r.startH + 1e-9)
    );
    if (blockIdx === -1) return false;
    const gap = lane[blockIdx];
    // Where this vehicle is realistically available again -- the job's own
    // end time plus its type's load/unload+spacing buffer. Deliberately NOT
    // rounded up to a clean mark the way ticksForShift rounds an open gap
    // tick's display start -- that's a cosmetic nicety for suggesting a
    // fresh, round-looking start time, not a real constraint, and applying
    // it here as one incorrectly rejected an already-known booking (a real,
    // fixed pickup time) that the raw buffer math actually clears in time for.
    const occupiedUntilH = dj.endH + w.requiredGapH;
    const before: SlotBlock[] = gap.startH < dj.startH - 1e-9 ? [{ ...gap, endH: dj.startH, isShiftEnd: false }] : [];
    // The buffer window itself, kept as its own gap block (not silently
    // dropped) so the lane's time coverage stays gapless for "now" tracking
    // -- it's excluded from being a placement target via `reserved` above
    // instead, not by leaving it out of the lane.
    const buffer: SlotBlock[] =
      occupiedUntilH > dj.endH + 1e-9 && gap.endH > dj.endH + 1e-9
        ? [{ ...gap, startH: dj.endH, endH: Math.min(occupiedUntilH, gap.endH), isShiftStart: false, isShiftEnd: false }]
        : [];
    // The tail of the gap tick beyond the buffer, if the job+buffer is
    // shorter than that tick -- same gapless-coverage reasoning.
    const after: SlotBlock[] = gap.endH > occupiedUntilH + 1e-9 ? [{ ...gap, startH: occupiedUntilH, isShiftStart: false }] : [];
    const occupyingBlocks = buildOccupyingBlocks(
      dj,
      gap.lane,
      gap.vehicleNumber,
      gap.shiftStartH,
      before.length === 0 && gap.isShiftStart,
      buffer.length === 0 && after.length === 0 && gap.isShiftEnd
    );
    const replacement = [...before, ...occupyingBlocks, ...buffer, ...after];
    lane.splice(blockIdx, 1, ...replacement);
    if (!w.reservedByLane.has(laneIdx)) w.reservedByLane.set(laneIdx, []);
    w.reservedByLane.get(laneIdx)!.push({ startH: dj.endH, endH: occupiedUntilH });
    // A booking's real duration (plus buffer) commonly runs past the single
    // tick it was seated into -- absorb/trim any FURTHER gap ticks (beyond
    // the one it was placed into, already handled by `after` above) that
    // occupiedUntilH still overlaps, so the sheet doesn't show the vehicle
    // as free before its buffer clears. Stop at a real job block; that's a
    // pre-existing data conflict, not ours to resolve here.
    const i = blockIdx + replacement.length;
    while (i < lane.length && lane[i].kind === "gap" && lane[i].startH < occupiedUntilH - 1e-9) {
      if (lane[i].endH <= occupiedUntilH + 1e-9) {
        lane.splice(i, 1);
      } else {
        lane[i] = { ...lane[i], startH: occupiedUntilH };
        break;
      }
    }
    return true;
  };

  for (const dj of sortedJobs) {
    const requiredType = vehicleTypeForRequestedLabel(dj.job);
    // "Not specified"/unmapped means no particular vehicle type is required
    // (e.g. TaxiCaller's "Next Available" / vtype 0) -- that's not "nowhere
    // to place it", it's "anywhere ordinary is fine". Excludes Wheelchair
    // specifically: that's a scarce, specialised resource that should stay
    // reserved for bookings that actually need it, not soaked up by
    // next-available jobs that never asked for one.
    const candidateTypeNames = requiredType
      ? Array.from(new Set([requiredType, ...getCompatibleVehicleTypes(requiredType, compatMatrix)]))
      : working.map((w) => w.type.vehicle_type).filter((name) => name !== "Wheelchair");

    let placed = false;

    // A partially-dispatched multi-leg group (some legs already have a
    // real vehicle_number) tries that SAME vehicle's own lane first,
    // regardless of vehicle-type compatibility -- it's already the real
    // car doing this passenger's trip, not a type match to search for.
    // dj.startH/endH reflect only the STILL-unassigned legs' own nominal
    // (originally requested) time -- commonly the same instant every leg of
    // the group was requested at, long since passed once part of the group
    // is already for-real dispatched. Anchor the remaining legs to wherever
    // this real vehicle's already-known work actually finishes instead, so
    // they're seated as a genuine continuation of its current job, not
    // (failingly) squeezed into a moment that vehicle isn't free at anymore.
    if (dj.assignedVehicleNumber) {
      outer: for (const w of working) {
        for (let laneIdx = 0; laneIdx < w.lanes.length; laneIdx++) {
          const lane = w.lanes[laneIdx];
          if (!lane.some((b) => b.vehicleNumber === dj.assignedVehicleNumber)) continue;
          let lastKnownEndH = dj.startH;
          for (const b of lane) {
            if (b.kind !== "gap" && b.vehicleNumber === dj.assignedVehicleNumber) lastKnownEndH = Math.max(lastKnownEndH, b.endH);
          }
          const durationH = dj.endH - dj.startH;
          const shiftedDj = lastKnownEndH > dj.startH + 1e-9 ? { ...dj, startH: lastKnownEndH, endH: lastKnownEndH + durationH } : dj;
          if (trySeatInLane(w, laneIdx, shiftedDj)) {
            w.nextLaneIdx = (laneIdx + 1) % w.lanes.length;
            placed = true;
          }
          break outer; // this is the one lane that vehicle belongs to -- nothing else to try even if seating failed
        }
      }
    }

    if (!placed) {
      for (const typeName of candidateTypeNames) {
        const w = byTypeName.get(typeName);
        if (!w) continue;
        const laneCount = w.lanes.length;
        for (let offset = 0; offset < laneCount; offset++) {
          const laneIdx = (w.nextLaneIdx + offset) % laneCount;
          if (trySeatInLane(w, laneIdx, dj)) {
            w.nextLaneIdx = (laneIdx + 1) % laneCount;
            placed = true;
            break;
          }
        }
        if (placed) break;
      }
    }

    if (!placed) {
      // No open gap anywhere it could go -- park it in overflow under its
      // required type, or (no specific type required) whichever type was
      // tried first, rather than the job vanishing. Still attributed to the
      // group's already-known real vehicle, if any, so it stays visually
      // grouped instead of looking unassigned.
      const w = byTypeName.get(requiredType ?? candidateTypeNames[0]);
      if (w) {
        if (!w.hasOverflow) w.lanes.push([]);
        const overflowLane = w.lanes[w.lanes.length - 1];
        overflowLane.push(...buildOccupyingBlocks(dj, w.lanes.length - 1, dj.assignedVehicleNumber ?? null, dj.startH, false, false));
        w.hasOverflow = true;
      }
    }
  }

  return working.map((w) => ({ type: w.type, sheet: { laneCount: w.lanes.length, lanes: w.lanes, hasOverflow: w.hasOverflow } }));
}

/**
 * Cross-fleet analog of placeUnassignedJobs, shared by the STS and TTC tabs.
 * This work isn't grouped by vehicle type -- any vehicle working that
 * company's own shift-prefix (STS_ or TTC_) can take any of its jobs, so
 * there's no type/compatibility routing to do, just a straight gap-seeking
 * placement into the tab's own lanes (round-robining across them the same
 * way placeUnassignedJobs does within one type). Only called for a
 * cross-fleet tab's single-type sheet -- see fetchCrossFleetCapacity in
 * utils.ts for why these jobs arrive separately from the sheet's normal
 * `jobs` list instead of being matched to a lane by vehicle_number like
 * everything else.
 */
export function placeUnassignedCrossFleetJobs(
  sheet: TypedSlotSheet,
  unassignedJobs: FleetJobRow[],
  dateStr: string
): TypedSlotSheet {
  const lanes = sheet.sheet.lanes.map((lane) => [...lane]);
  let hasOverflow = sheet.sheet.hasOverflow;
  let nextLaneIdx = 0;

  const sortedJobs = unassignedJobs
    .filter((j) => nzDateStrFromIso(j.requested_pickup_at) === dateStr && j.Stage !== "Cancelled")
    .map((job) => ({ job, ...computeJobTimes(job) }))
    .sort((a, b) => a.startH - b.startH);

  for (const dj of sortedJobs) {
    let placed = false;
    const laneCount = lanes.length;
    for (let offset = 0; offset < laneCount; offset++) {
      const laneIdx = (nextLaneIdx + offset) % laneCount;
      const lane = lanes[laneIdx];
      const blockIdx = lane.findIndex(
        (b) =>
          b.kind === "gap" &&
          b.startH <= dj.startH + 1e-9 &&
          b.endH > dj.startH + 1e-9 &&
          dj.startH - b.shiftStartH >= DRIVER_LOGON_LEAD_H - 1e-9
      );
      if (blockIdx !== -1) {
        const gap = lane[blockIdx];
        const before: SlotBlock[] = gap.startH < dj.startH - 1e-9 ? [{ ...gap, endH: dj.startH, isShiftEnd: false }] : [];
        // Same gapless-coverage reasoning as placeUnassignedJobs -- keep the
        // tail of the gap tick the job was placed into instead of dropping it.
        const after: SlotBlock[] = gap.endH > dj.endH + 1e-9 ? [{ ...gap, startH: dj.endH, isShiftStart: false }] : [];
        const jobBlock: SlotBlock = {
          kind: "job",
          job: dj.job,
          lane: gap.lane,
          startH: dj.startH,
          endH: dj.endH,
          vehicleNumber: gap.vehicleNumber,
          isShiftStart: before.length === 0 && gap.isShiftStart,
          isShiftEnd: after.length === 0 && gap.isShiftEnd,
          shiftStartH: gap.shiftStartH,
        };
        const replacement = [...before, jobBlock, ...after];
        lane.splice(blockIdx, 1, ...replacement);
        const i = blockIdx + replacement.length;
        while (i < lane.length && lane[i].kind === "gap" && lane[i].startH < dj.endH - 1e-9) {
          if (lane[i].endH <= dj.endH + 1e-9) {
            lane.splice(i, 1);
          } else {
            lane[i] = { ...lane[i], startH: dj.endH };
            break;
          }
        }
        nextLaneIdx = (laneIdx + 1) % laneCount;
        placed = true;
        break;
      }
    }

    if (!placed) {
      if (!hasOverflow) lanes.push([]);
      const overflowLane = lanes[lanes.length - 1];
      overflowLane.push({
        kind: "job",
        job: dj.job,
        lane: lanes.length - 1,
        startH: dj.startH,
        endH: dj.endH,
        vehicleNumber: null,
        isShiftStart: false,
        isShiftEnd: false,
        shiftStartH: dj.startH,
      });
      hasOverflow = true;
    }
  }

  return { type: sheet.type, sheet: { laneCount: lanes.length, lanes, hasOverflow } };
}
